import { StreamResolutions, StreamResolutionUtil } from '../../utility/StreamResolutionUtil';
import { StreamType } from '../../../api/models/Shared.types';
import { QuoteType, VodAssetStreamType } from '../detail.types';
import { timeStringToMs } from '../../utility/DateFormatter';
import {
  IStreamPriceResponse3,
  IValidQuotePriceResponse3,
  IVodPriceBundleResponse,
  IVodPriceResponse3,
  IVodUpsellProduct2,
} from '../../../api/models/AndromeTV.types';

export class OttStream {
  validQuotes: IValidQuotePriceResponse3[];
  stream: IStreamPriceResponse3;
  constructor(stream: IStreamPriceResponse3, validQuotes: IValidQuotePriceResponse3[]) {
    this.stream = stream;
    this.validQuotes = validQuotes;
  }
  selectValidQuote() {
    // first quote that is usable
    return this.validQuotes[0];
  }

  getStreamId() {
    return this.stream ? this.stream.streamId : undefined;
  }

  getStreamType() {
    return this.stream ? (this.stream.type as StreamType) : undefined;
  }
}

export class VodStreamRights {
  private vodId: string;
  private quotes: IValidQuotePriceResponse3[];
  private ottPrice: IVodPriceResponse3;
  trailerStreams: IStreamPriceResponse3[];
  private watchableOttStreams: OttStream[];
  rentableOttStreams: OttStream[];
  upsell: IVodUpsellProduct2 | undefined;
  constructor(vodId: string, quotes: IValidQuotePriceResponse3[] | undefined, ottPrice: IVodPriceResponse3) {
    this.vodId = vodId;
    this.quotes = quotes || [];
    this.ottPrice = ottPrice;

    this.trailerStreams = [];
    this.watchableOttStreams = [];
    this.rentableOttStreams = [];
    this.upsell = undefined;

    this._calculateStreamRights();
  }

  calculateStreamRightsForNewQuotes(quotes: IValidQuotePriceResponse3[]) {
    this.trailerStreams = [];
    this.watchableOttStreams = [];
    this.rentableOttStreams = [];
    this.quotes = quotes;

    this._calculateStreamRights();
  }

  _calculateStreamRights() {
    this._checkForTrailerStreams(this.ottPrice);

    this._checkForPrice(this.quotes, this.ottPrice);
  }

  ottStreamsLeftToRent() {
    return this.rentableOttStreams !== undefined && this.rentableOttStreams.length !== 0;
  }

  getWatchableOttStream() {
    let stream = this.watchableOttStreams.find(str => str.stream.resolution === StreamResolutions.FOURK);
    if (stream) {
      return stream;
    }
    stream = this.watchableOttStreams.find(str => str.stream.resolution === StreamResolutions.HD);
    if (stream) {
      return stream;
    }

    stream = this.watchableOttStreams.find(str => str.stream.resolution === StreamResolutions.SD);
    return stream;
  }

  hasWatchableOttSVOD() {
    return (
      this.canWatch() &&
      this.watchableOttStreams.some(ottStream => ottStream.stream && ottStream.stream.type === StreamType.SVOD)
    );
  }

  hasWatchableOttTVOD() {
    return (
      this.canWatch() &&
      this.watchableOttStreams.some(ottStream => ottStream.stream && ottStream.stream.type === StreamType.TVOD)
    );
  }

  hasWatchableOttFVOD() {
    return (
      this.canWatch() &&
      this.watchableOttStreams.some(ottStream => ottStream.stream && ottStream.stream.type === StreamType.FVOD)
    );
  }

  hasTrailer() {
    return this.trailerStreams.length > 0;
  }

  canRent() {
    return this.rentableOttStreams.length > 0;
  }

  canWatch() {
    return this.watchableOttStreams.length > 0;
  }

  hasBundles() {
    return this.ottPrice.bundles && this.ottPrice.bundles.length > 0;
  }

  isWatchableBecauseOfBundle() {
    return (
      this.canWatch() &&
      this.watchableOttStreams.some(
        ottStream => ottStream.validQuotes.some(quote => quote.quoteType === QuoteType.BUNDLE) //TODO replace by enum
      )
    );
  }

  _getValidQuotesForTVODS(
    allStreams: IStreamPriceResponse3[], //: StreamPriceResponse[],
    stream: IStreamPriceResponse3, //: StreamPriceResponse,
    quotes: IValidQuotePriceResponse3[], //: ValidQuotePriceResponse[],
    bundles: IVodPriceBundleResponse[] | undefined //: VodPriceBundleResponse[],
  ) {
    //: ValidQuotePriceResponse[]
    const now = new Date().getTime();
    // find tvod quote valid for current quality or quote quality higher than current stream quality
    const tvodQuotes = quotes.filter(
      quote =>
        quote.quoteType === QuoteType.TVOD &&
        quote.entryId === this.vodId &&
        (quote.assetId === stream.streamId ||
          allStreams.some(
            otherStream =>
              quote.assetId === otherStream.streamId &&
              StreamResolutionUtil.toInt(otherStream.resolution) > StreamResolutionUtil.toInt(stream.resolution)
          )) &&
        timeStringToMs(quote.startTime) < now &&
        timeStringToMs(quote.endTime) > now
    );

    let bundleQuotes: IValidQuotePriceResponse3[] = [];

    if (bundles) {
      bundles.forEach(bundle => {
        if (bundle.resolutions) {
          // select filtered quotes for this bundle, for this stream
          const filteredQuotes = quotes.filter(
            quote =>
              quote.entryId === bundle.bundleId &&
              timeStringToMs(quote.endTime) > now &&
              bundle.resolutions?.some(
                resolution =>
                  quote.assetId === resolution.id &&
                  StreamResolutionUtil.toInt(resolution.resolution) >= StreamResolutionUtil.toInt(stream.resolution)
              )
          );
          if (filteredQuotes) {
            bundleQuotes = bundleQuotes.concat(filteredQuotes);
          }
        }
      });
    }

    return tvodQuotes.concat(bundleQuotes);
  }

  _checkForPrice(quotes: IValidQuotePriceResponse3[], price: IVodPriceResponse3) {
    // Set upsell information
    if (
      !this.upsell ||
      (price.upsell &&
        price.upsell.preference &&
        this.upsell.preference &&
        price.upsell.preference > this.upsell.preference)
    ) {
      this.upsell = price.upsell;
    }

    if (price.streams && price.streams.length > 0) {
      price.streams.forEach(stream => {
        if (stream.streamType === VodAssetStreamType.FULL) {
          const validQuotes = this._getValidQuotesForTVODS(price.streams!, stream, quotes, price.bundles);

          // stream is watchable if:
          if (
            // vod is free
            stream.type === StreamType.FVOD ||
            // or there is free higher quality
            price.streams!.some(
              str =>
                str.streamType === VodAssetStreamType.FULL &&
                str.type === StreamType.FVOD &&
                StreamResolutionUtil.toInt(str.resolution) > StreamResolutionUtil.toInt(stream.resolution)
            ) ||
            // or it is an svod
            stream.type === StreamType.SVOD ||
            // or there is a valid quote
            validQuotes.length > 0
          ) {
            this.watchableOttStreams.push(new OttStream(stream, validQuotes));

            // if on ott no valid quotes for steam and it is a TVOD, we can rent this stream
          } else if (validQuotes.length === 0 && stream.type === StreamType.TVOD) {
            this.rentableOttStreams.push(new OttStream(stream, []));
          }
        }
      });
    }

    this.rentableOttStreams.sort((a, b) => {
      if (StreamResolutionUtil.toInt(a.stream.resolution) > StreamResolutionUtil.toInt(b.stream.resolution)) {
        return -1;
      } else if (StreamResolutionUtil.toInt(a.stream.resolution) < StreamResolutionUtil.toInt(b.stream.resolution)) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  _checkForTrailerStreams(ottPrice: IVodPriceResponse3) {
    if (ottPrice.streams) {
      ottPrice.streams.forEach(stream => {
        if (
          stream.streamType === VodAssetStreamType.TRAILER &&
          (stream.type === StreamType.FVOD || stream.type === StreamType.SVOD)
        ) {
          this.trailerStreams.push(stream);
        }
      });
    }
  }

  hasUpsell() {
    return !this.canWatch() && this.upsell !== undefined;
  }

  getFullVodStreams() {
    let fullVodStreams: IStreamPriceResponse3[] = [];

    if (this.ottPrice && this.ottPrice.streams) {
      fullVodStreams = fullVodStreams.concat(
        this.ottPrice.streams.filter(stream => stream.streamType === VodAssetStreamType.FULL)
      );
    }

    return fullVodStreams;
  }

  getLatestValiQuoteForType(quoteType: string) {
    const quotesOfType: IValidQuotePriceResponse3[] = [];

    this.watchableOttStreams.forEach(ottStream =>
      ottStream.validQuotes.forEach(quote => {
        if (quote.quoteType === quoteType) {
          quotesOfType.push(quote);
        }
      })
    );
    return this._getLastValidQuote(quotesOfType);
  }

  getBundles() {
    return this.ottPrice && this.ottPrice.bundles ? this.ottPrice.bundles : [];
  }

  getBundle(bundleId: string | undefined) {
    if (!bundleId) return undefined;
    return this.ottPrice.bundles?.find(ottBundle => ottBundle.bundleId === bundleId);
  }

  getLatestValidQuoteForBundle(bundleId: string): IValidQuotePriceResponse3 | undefined {
    const quotesOfTypeForBundle: IValidQuotePriceResponse3[] = [];

    this.watchableOttStreams.forEach(ottStream =>
      ottStream.validQuotes.forEach(quote => {
        if (quote.quoteType === QuoteType.BUNDLE && quote.entryId === bundleId) {
          quotesOfTypeForBundle.push(quote);
        }
      })
    );

    return this._getLastValidQuote(quotesOfTypeForBundle);
  }

  _getLastValidQuote(quotes: IValidQuotePriceResponse3[]): IValidQuotePriceResponse3 | undefined {
    let latestValidQuote: IValidQuotePriceResponse3 | undefined = undefined;
    quotes.forEach(quote => {
      if (!latestValidQuote || parseInt(quote.endTime, 10) > parseInt(latestValidQuote.endTime, 10)) {
        latestValidQuote = quote;
      }
    });

    return latestValidQuote;
  }
}
